import { useEffect } from "react";
import { buildAbsoluteUrl, getDefaultRobots, getDefaultSeo, resolveImageUrl } from "utils/seo";

const ensureMetaTag = (attribute, key, content) => {
  if (!content) return;

  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const ensureLinkTag = (rel, href) => {
  if (!href) return;

  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

const ensureJsonLd = (schema) => {
  const existing = document.getElementById("seo-json-ld");
  if (!schema) {
    if (existing) existing.remove();
    return;
  }

  const tag = existing || document.createElement("script");
  tag.id = "seo-json-ld";
  tag.type = "application/ld+json";
  tag.text = JSON.stringify(schema);

  if (!existing) {
    document.head.appendChild(tag);
  }
};

function Seo({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  robots = "index,follow",
  schema = null,
}) {
  useEffect(() => {
    const defaults = getDefaultSeo();
    const resolvedTitle = title || defaults.title;
    const resolvedDescription = description || defaults.description;
    const canonicalUrl = buildAbsoluteUrl(canonicalPath || "/");
    const resolvedImage = resolveImageUrl(image || defaults.image);
    const resolvedRobots = robots === "index,follow" ? getDefaultRobots() : robots;

    document.title = resolvedTitle;

    ensureMetaTag("name", "description", resolvedDescription);
    ensureMetaTag("name", "robots", resolvedRobots);
    ensureMetaTag("property", "og:site_name", defaults.siteName);
    ensureMetaTag("property", "og:type", type);
    ensureMetaTag("property", "og:title", resolvedTitle);
    ensureMetaTag("property", "og:description", resolvedDescription);
    ensureMetaTag("property", "og:url", canonicalUrl);
    ensureMetaTag("property", "og:image", resolvedImage);
    ensureMetaTag("name", "twitter:card", "summary_large_image");
    ensureMetaTag("name", "twitter:title", resolvedTitle);
    ensureMetaTag("name", "twitter:description", resolvedDescription);
    ensureMetaTag("name", "twitter:image", resolvedImage);
    ensureLinkTag("canonical", canonicalUrl);
    ensureJsonLd(schema);
  }, [canonicalPath, description, image, robots, schema, title, type]);

  return null;
}

export default Seo;
